'use client';

import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { useLocale } from 'next-intl';

import { useUserSession } from '@/hooks/use-user-session';
import { db, firebaseAuth } from '@/libs/firebase/config';

const STORAGE_KEY = 'asktheoracle:preferredLocale';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const locale = useLocale();
  const userUid = useUserSession();
  const [userProfile, setUserProfile] = useState(null);
  const [preferredLocale, setPreferredLocale] = useState(locale);
  const [isLoading, setIsLoading] = useState(true);
  const lastPersistedLocaleRef = useRef(null);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const storedLocale = window.localStorage.getItem(STORAGE_KEY);

    if (!userUid && storedLocale) {
      setPreferredLocale(storedLocale);
    }
  }, [userUid]);

  useEffect(() => {
    if (!locale) return;
    setPreferredLocale((current) => (current === locale ? current : locale));
  }, [locale]);

  useEffect(() => {
    if (!userUid) {
      setUserProfile(null);
      setIsLoading(false);
      return;
    }

    let cancelled = false;

    async function loadUserProfile(uid) {
      setIsLoading(true);
      try {
        const docRef = doc(db, 'users', uid);
        const snapshot = await getDoc(docRef);

        if (cancelled) return;

        if (snapshot.exists()) {
          const data = snapshot.data();

          setUserProfile({ id: uid, ...data });

          if (data?.preferredLocale) {
            lastPersistedLocaleRef.current = data.preferredLocale;
            setPreferredLocale(data.preferredLocale);
          }
        } else {
          setUserProfile(null);
          setPreferredLocale(locale);
        }
      } catch (error) {
        console.error('AuthContext: Failed to load user profile', error);
        setUserProfile(null);
        setPreferredLocale(locale);
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    }

    loadUserProfile(userUid);

    return () => {
      cancelled = true;
    };
  }, [userUid, locale]);

  useEffect(() => {
    if (typeof window === 'undefined' || !preferredLocale) return;

    window.localStorage.setItem(STORAGE_KEY, preferredLocale);
  }, [preferredLocale]);

  useEffect(() => {
    if (!userUid || !preferredLocale) return;
    if (lastPersistedLocaleRef.current === preferredLocale) return;

    let active = true;

    async function persistPreferredLocale() {
      try {
        const docRef = doc(db, 'users', userUid);
        await setDoc(docRef, { preferredLocale }, { merge: true });
        if (active) {
          lastPersistedLocaleRef.current = preferredLocale;
        }
      } catch (error) {
        console.error('AuthContext: Failed to persist preferred locale', error);
      }
    }

    persistPreferredLocale();

    return () => {
      active = false;
    };
  }, [userUid, preferredLocale]);

  useEffect(() => {
    if (!userUid) {
      lastPersistedLocaleRef.current = null;
    }
  }, [userUid]);

  const updatePreferredLocale = useCallback((nextLocale) => {
    if (!nextLocale) return;
    setPreferredLocale(nextLocale);
  }, []);

  const value = useMemo(
    () => ({
      userUid,
      firebaseUser: firebaseAuth.currentUser,
      userProfile,
      isLoading,
      preferredLocale,
      updatePreferredLocale,
      locale,
    }),
    [userUid, userProfile, isLoading, preferredLocale, updatePreferredLocale, locale],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }

  return context;
}


